import { useContext } from 'react';
import { useLocation } from 'react-router';
import { store } from 'components/Store';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { RegionMetaData } from 'components/Dashboard/Analytics/ui/AnalyticsFilters/PartyMetadata';
import { chartDataOptions, createIncludeRepostsOption } from './createAnalyticsState';
import useSelectedCountriesFilters from './useSelectedCountriesFilters';
import useAnalyticsIntervalLimit from './useAnalyticsIntervalLimit';
import { useAuth0 } from '@auth0/auth0-react';
import getAppSite from 'utils/getAppSite';
import time from 'utils/time';
import graphDataSections from 'components/Dashboard/Analytics/graphDataSections';
import useAnalyticsCommonHelpers from 'components/Dashboard/Analytics/hooks/useAnalyticsCommonHelpers';
import useAnalyticsApiHelpers from 'components/Dashboard/Analytics/hooks/useAnalyticsApiHelpers';
import { compileData } from 'components/Dashboard/Analytics/hooks/useCompileMentionsDataFunctions';

time.normalizeDateLocale();

// Written questions are split across two entity types on the backend (WrittenQuestion for UK,
// CountrySpecificWrittenQuestion for the devolved nations). graphDataSections.js carries one
// section per nation; this just picks which of those are selected.
const isWrittenQuestionsSectionSelected = (item, { UKSelected, WalesSelected, ScotlandSelected }) => {
  if (item.name === 'Written questions') return UKSelected;
  if (item.name === 'Written questions (Wales)') return WalesSelected;
  if (item.name === 'Written questions (Scotland)') return ScotlandSelected;
  return false;
};

const useCallMentionsByTopic = (props) => {
  const { teamId, selectedCountriesProps, chartType, tweetsEnabled } = props ?? {};
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists, analyticsState, activePlan } = state;
  const { isAuthenticated } = useAuth0();
  const { enableTwitter } = activePlan ?? {};
  const { currentView, chartDataOptionSelected, selectedHouseOptions, chartDataOptionSelectedTweets } = analyticsState;
  const includeXRepostsTweetsSection =
    props?.includeXRepostsTweetsSection ?? analyticsState?.includeXRepostsTweetsSection;
  const activeDateInterval =
    chartType === 'Tweets' ? analyticsState?.tweetsDateInterval : analyticsState?.activeDateInterval;
  const keywordsListsToUse = keywordsLists.filter((item) => item.id !== null);
  const topicParameter = getUrlParam('topic-id');

  const { addCommonAnalyticsProperties, createAnalyticsPathname, parliamentaryPositionsFilters } =
    useAnalyticsApiHelpers();
  const { transformInterval, createFilters, mergeResults, createKey, TweetsFilters } = useAnalyticsCommonHelpers();
  const { generalApiCall } = useGeneralApiCall();
  const { validateDataForLoggedOutUsers, conditionOfPublicApi } = useAnalyticsIntervalLimit();
  const location = useLocation();
  const {
    getSelectedCountriesFilter,
    UKSelected,
    WalesSelected,
    ScotlandSelected,
    getCountrySpecificFilters,
    getWrittenQuestionCountryFilters,
  } = useSelectedCountriesFilters({ selectedCountriesProps });

  const stakeholderContributions = location.pathname.includes('/mentions') || chartType === 'Mentions';
  const defaultTopic = topicParameter
    ? keywordsListsToUse.find((item) => item.id === parseInt(topicParameter))
    : keywordsListsToUse[0];

  const callMentionsByTopic = async ({
    view = currentView,
    topic = defaultTopic,
    dateInterval = activeDateInterval.filter,
    chartDataOption = stakeholderContributions ? chartDataOptionSelected : chartDataOptionSelectedTweets,
    stakeholderContributionsGraph,
    source,
    freeSearch,
    stakeholderId,
    includeTweetsOnMentions, //AE: This is for stakeholder page where we want to show Tweets data on single chart
  }) => {
    try {
      if (topic || freeSearch) {
        let value;
        let rawData;

        if (chartDataOption === 'Contribution type' || chartDataOption === 'Overall') {
          if ((stakeholderContributions || stakeholderContributionsGraph) && chartType !== 'Tweets') {
            let parliamentaryRecord = await parliamentaryRecordCall({
              topic,
              view,
              dateInterval,
              source,
              freeSearch,
              stakeholderId,
            });
            if (!!parliamentaryRecord) {
              let writtenQuestions = await writtenQuestionsCall({
                topic,
                view,
                dateInterval,
                source,
                freeSearch,
                stakeholderId,
              });
              value = {
                'Spoken parliamentary contributions': mergeResults(
                  parliamentaryRecord?.series?.find((item) => item.bucket === 'crec')?.datapoints,
                  mergeResults(
                    parliamentaryRecord?.series?.find((item) => item.bucket === 'parliamentarycontribution')
                      ?.datapoints,

                    mergeResults(
                      parliamentaryRecord?.series?.find(
                        (item) => item.bucket === 'walescountryspecificparliamentarycontribution'
                      )?.datapoints,
                      parliamentaryRecord?.series?.find(
                        (item) => item.bucket === 'scotlandcountryspecificparliamentarycontribution'
                      )?.datapoints
                    )
                  )
                ),
                ...writtenQuestions?.value,
              };
              if (WalesSelected) {
                value = {
                  ...value,
                  'Committee contributions': parliamentaryRecord?.series?.find(
                    (item) => item.bucket === 'seneddcommitteetranscriptcontribution'
                  )?.datapoints,
                };
              }
              if (UKSelected) {
                value = {
                  ...value,
                  EDMs: parliamentaryRecord?.series?.find((item) => item.bucket === 'edm')?.datapoints,
                };
              }
            }
          }
          if (
            import.meta.env.VITE_APP_ENABLE_TWITTER === 'true' &&
            (enableTwitter || tweetsEnabled) &&
            (chartType === 'Tweets' || includeTweetsOnMentions)
          ) {
            let tweets = await tweetsCall({
              topic,
              view,
              dateInterval,
              source,
              freeSearch,
              stakeholderId,
              includeXRepostsTweetsSection,
            });
            if (!!tweets) {
              value = {
                ...value,
                Tweets: tweets?.series?.find((item) => item.bucket === '0')?.datapoints,
              };
              if (createIncludeRepostsOption({ tweetsDateInterval: { filter: dateInterval } })) {
                value = {
                  ...value,
                  Retweets: tweets?.series?.find((item) => item.bucket === '1')?.datapoints,
                };
              }
            }
          }
          rawData = value;
        } else {
          let chartData = await callGetGraphData({
            topic,
            view,
            dateInterval,
            chartDataOption,
            stakeholderContributionsGraph,
            source,
            freeSearch,
            includeXRepostsTweetsSection,
          });
          if (!!chartData) {
            value = chartData.buckets;
            rawData = chartData.rawResults;
          }
        }

        let totalData = compileData({ value }, analyticsState);
        let compiledDataRaw = totalData.raw;
        let data = {
          labels: compiledDataRaw.map((item) => item.date),
          value:
            chartDataOption === 'Overall' && chartType !== 'Tweets'
              ? { [`Total contributions`]: totalData?.raw }
              : value,
        };
        //AE: This was creating an error when there was no results on a topic/filter: Object.keys(value)?.length > 0
        if (!!value) {
          return { data, rawData };
        }
      }
    } catch (error) {}
  };

  const parliamentaryRecordCall = async ({ topic, view, dateInterval, source, freeSearch, stakeholderId }) => {
    try {
      const pathname = createAnalyticsPathname({ teamId, freeSearch, conditionOfPublicApi, topic });
      const method = 'post';
      let requestProperties = {
        type: 'ParliamentaryRecord',
        interval: transformInterval(view),
        filters: [...createFilters(dateInterval, view)],
        bucketField: 'contentType',
        order: 'asc',
      };

      if (getAppSite() === 'global') {
        requestProperties = {
          ...requestProperties,
          filters: [
            ...requestProperties?.filters,
            ...parliamentaryPositionsFilters,
            ...getSelectedCountriesFilter(true),
          ],
        };
      } else if (getAppSite() === 'usa') {
        requestProperties = {
          ...requestProperties,
          filters: [
            ...requestProperties?.filters,
            {
              field: 'contentType',
              value: 'CREC',
              operator: 'str_eq',
            },
          ],
        };
      }

      requestProperties = addCommonAnalyticsProperties(requestProperties, { topic, freeSearch, teamId, stakeholderId });

      let results = await generalApiCall({
        pathname,
        method,
        requestProperties,
        needsAuthentication: !teamId || (freeSearch && isAuthenticated),
        notShowErrorMessage: true,
        requestSource: source,
        returnError: true,
      });
      if (!validateDataForLoggedOutUsers(results)) {
        return [];
      }
      if (!!results) {
        return results;
      }
    } catch (error) {}
  };

  // Written questions across nations now come back from a single request bucketed by
  // country (bucket values are the lowercased country name, e.g. 'united kingdom', 'wales'),
  // instead of one request per nation. Same country-filter pattern as parliamentaryRecordCall,
  // so Northern Ireland (not supported for this section) is never fetched.
  // `value` merges the selected countries into one series for the chart; `raw` keeps them
  // separate (one key per nation) for drill-down.
  const writtenQuestionsCall = async ({ topic, view, dateInterval, source, freeSearch, stakeholderId }) => {
    try {
      const pathname = createAnalyticsPathname({ teamId, freeSearch, conditionOfPublicApi, topic });
      const method = 'post';
      let filters = [...createFilters(dateInterval, view)];
      if (getAppSite() === 'global') {
        filters = [...filters, ...parliamentaryPositionsFilters, ...getWrittenQuestionCountryFilters()];
      }

      let requestProperties = {
        type: 'WrittenQuestion',
        interval: transformInterval(view),
        filters,
        bucketField: 'country',
        order: 'asc',
      };

      requestProperties = addCommonAnalyticsProperties(requestProperties, {
        topic,
        freeSearch,
        teamId,
        stakeholderId,
      });

      let results = await generalApiCall({
        pathname,
        method,
        requestProperties,
        needsAuthentication: !teamId || (freeSearch && isAuthenticated),
        notShowErrorMessage: true,
        requestSource: source,
        returnError: true,
      });
      if (!validateDataForLoggedOutUsers(results) || !results) return {};

      const countryBuckets = [
        { name: 'Written questions', bucket: 'united kingdom' },
        { name: 'Written questions (Wales)', bucket: 'wales' },
        { name: 'Written questions (Scotland)', bucket: 'scotland' },
      ];

      let raw = {};
      countryBuckets.forEach(({ name, bucket }) => {
        raw[name] = results?.series?.find((item) => item.bucket?.toLowerCase() === bucket)?.datapoints ?? [];
      });

      let merged = Object.values(raw).reduce((acc, datapoints) => mergeResults(acc, datapoints), undefined);

      return { value: merged ? { 'Written questions': merged } : {} };
    } catch (error) {}
  };

  const tweetsCall = async ({
    topic,
    view,
    dateInterval,
    source,
    freeSearch,
    stakeholderId,
    includeXRepostsTweetsSection,
  }) => {
    try {
      const pathname = createAnalyticsPathname({ teamId, freeSearch, conditionOfPublicApi, topic });
      const method = 'post';
      let filters = createFilters(dateInterval, view);
      if (getAppSite() === 'global') {
        filters = [...filters, ...parliamentaryPositionsFilters];
      }
      filters = [...filters, ...TweetsFilters];

      if (chartType === 'Tweets') {
        if (includeXRepostsTweetsSection) {
          filters = filters.filter((item) => item.field !== 'isRetweet');
        } else {
          filters = [...filters, { field: 'isRetweet', value: 'false', operator: 'str_eq' }];
        }
      }

      let requestProperties = {
        type: 'Tweet',
        interval: transformInterval(view),
        filters,
        bucketField: 'isRetweet',
        order: 'asc',
      };
      requestProperties = addCommonAnalyticsProperties(requestProperties, {
        topic,
        freeSearch,
        teamId,
        stakeholderId,
        tweets: true,
      });
      let results = await generalApiCall({
        pathname,
        method,
        requestProperties,
        needsAuthentication: !teamId || (freeSearch && isAuthenticated),
        notShowErrorMessage: true,
        requestSource: source,
        returnError: true,
      });
      if (!!results && validateDataForLoggedOutUsers(results)) {
        return results;
      }
    } catch (error) {}
  };

  const individualGetDataChartApiCall = async ({
    topic,
    view,
    dateInterval,
    section,
    optionData,
    source,
    freeSearch,
    includeXRepostsTweetsSection,
  }) => {
    try {
      const { field, value, type, additionalFilters } = section;
      const pathname = createAnalyticsPathname({ teamId, freeSearch, conditionOfPublicApi, topic });
      const method = 'post';
      let filters = [...createFilters(dateInterval, view)];

      if (getAppSite() === 'global') {
        filters = [...filters, ...parliamentaryPositionsFilters, ...getCountrySpecificFilters({ value, field })];
      } else if (getAppSite() === 'usa') {
        if (type?.toLowerCase() !== 'tweet') {
          filters = [
            ...filters,
            {
              field: 'contentType',
              value: 'CREC',
              operator: 'str_eq',
            },
          ];
        }
        if (chartDataOptionSelected === 'House') {
          const houseOptions = selectedHouseOptions.map((option) => ({
            field: 'house',
            value: option.toLowerCase(),
            operator: 'str_eq',
          }));
          filters = [...filters, ...houseOptions];
        }
      }

      if (chartType === 'Tweets' && !includeXRepostsTweetsSection) {
        filters = [...filters, { field: 'isRetweet', value: 'false', operator: 'str_eq' }];
      }

      if (optionData?.filters) {
        filters = [...filters, ...optionData.filters];
      }

      if (additionalFilters) {
        filters = [...filters, ...additionalFilters];
      }
      let requestProperties = {
        type: type,
        interval: transformInterval(view),
        filters,
        bucketField: optionData?.bucketField,
        order: 'asc',
      };

      requestProperties = addCommonAnalyticsProperties(requestProperties, { topic, freeSearch, teamId });

      let results = await generalApiCall({
        pathname,
        method,
        requestProperties,
        needsAuthentication: !teamId || (freeSearch && isAuthenticated),
        notShowErrorMessage: true,
        requestSource: source,
      });

      if (!!results && validateDataForLoggedOutUsers(results)) {
        return results;
      }
    } catch (error) {}
  };

  const callGetGraphData = async ({
    view = currentView,
    topic = defaultTopic,
    dateInterval = activeDateInterval.filter,
    chartDataOption,
    stakeholderContributionsGraph,
    source,
    freeSearch,
    includeXRepostsTweetsSection,
  }) => {
    try {
      let finalResults = [];
      let rawResults = {};
      let optionData = chartDataOptions.find((item) => item.name === chartDataOption);
      let graphDataSectionsFiltered = graphDataSections.filter((item) => {
        if (item.type === 'Tweet' && (!enableTwitter || chartType !== 'Tweets')) {
          return false;
        }
        if (item.value === 'Edm') {
          return UKSelected && chartType !== 'Tweets';
        }
        if (item.value === 'SeneddCommitteeTranscriptContribution') {
          return WalesSelected && chartType !== 'Tweets';
        }
        if (item.name.startsWith('Written questions')) {
          return (
            isWrittenQuestionsSectionSelected(item, { UKSelected, WalesSelected, ScotlandSelected }) &&
            chartType !== 'Tweets'
          );
        }
        if (item.name === 'Retweets') {
          return false;
        }
        if (stakeholderContributions || stakeholderContributionsGraph) {
          return graphDataSections;
        } else {
          return item.type === 'Tweet' && enableTwitter;
        }
      });

      for (let i = 0; i < graphDataSectionsFiltered.length; i++) {
        let section = graphDataSectionsFiltered[i];
        let results = await individualGetDataChartApiCall({
          topic,
          view,
          dateInterval,
          section,
          optionData,
          source,
          freeSearch,
          includeXRepostsTweetsSection,
        });
        if (results) {
          finalResults.push(results.series);
          let rawResultsSimplified = {};
          JSON.parse(JSON.stringify(results.series)).forEach(
            (result) =>
              (rawResultsSimplified[createKey({ chartDataOption, item: result?.bucket })] = result?.datapoints)
          );
          rawResults[section?.name] = rawResultsSimplified;
        }
      }
      let initialBuckets = finalResults.flat().reduce((acc, item) => {
        let key = createKey({ chartDataOption, item: item.bucket });
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item.datapoints);

        return acc;
      }, {});
      let bucketKeys = Object.keys(initialBuckets);
      let buckets = initialBuckets;
      if (chartDataOption === 'Region') {
        buckets = {};
        RegionMetaData.orderingRegions.forEach((item) => {
          if (bucketKeys.includes(item)) {
            buckets[item] = initialBuckets[item];
          }
        });
      }

      Object.keys(buckets).forEach((bucket) => {
        let series = buckets[bucket];
        let datapoints = [];
        datapoints = series.flat().reduce((acc, item) => {
          let dateIndex = acc.findIndex((point) => point.date === item.date);
          if (dateIndex >= 0) {
            let { value } = acc[dateIndex];
            let newValue = item.value + value;
            acc[dateIndex].value = newValue;
          } else {
            acc.push(item);
          }
          return acc;
        }, []);
        let condition = optionData.showRowsFor ? !optionData.showRowsFor.includes(bucket) : false;
        if (condition) {
          delete buckets[bucket];
        } else {
          buckets[bucket] = datapoints;
        }
      });
      return { buckets, rawResults };
    } catch (error) {}
  };

  return {
    callMentionsByTopic,
    callGetGraphData,
  };
};

export default useCallMentionsByTopic;
